import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { Person } from 'src/app/_model/person';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';

import { PersonsService } from '../persons.service';
import { PersonsDaoService } from '../../dao/persons-dao.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads ims/Person entities for the branches currently available in the app
 */
@Injectable()
export class PersonsLogicService implements PersonsService {
  public persons$ = new BehaviorSubject<Person[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private personsDaoService: PersonsDaoService,
    private authenticationService: AuthenticationService,
    private branchesService: BranchesService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    // Unlike clear(), reset() also wipes locally created (not-yet-synced) persons.
    await this.personsDaoService.reset();
    await this.reloadPersons();
  }

  public async clear() {
    await this.personsDaoService.clear();
    await this.reloadPersons();
  }

  /**
   * Re-publishes the persons straight from the db. Always goes through the
   * dao rather than patching persons$ in place, so that the merge of synced
   * rows with their local overrides stays in the one place that knows about it.
   */
  private async reloadPersons(): Promise<void> {
    this.persons$.next(await this.personsDaoService.getPersons());
  }

  public async createPerson(person: Person): Promise<Person> {
    const created = await this.personsDaoService.createPerson(person);
    if (created) {
      this.persons$.next([...this.persons$.getValue(), created]);
    }
    return created;
  }

  public async updatePerson(person: Person): Promise<void> {
    if (person.isLocal()) {
      await this.personsDaoService.updateLocalPerson(person);
    } else {
      await this.personsDaoService.savePersonOverride(person);
    }
    await this.reloadPersons();
  }

  public async deletePerson(person: Person): Promise<void> {
    if (!person.isLocal()) {
      // Nothing to delete against - the next sync would bring it right back.
      this.logger.warn(
        this.constructor.name,
        'Refusing to delete a synced person',
        person.getImsId(),
      );
      return;
    }
    await this.personsDaoService.deleteLocalPerson(person.id);
    await this.reloadPersons();
  }

  public getPersonById(id: IMSId): Person {
    if (id === undefined) {
      return undefined;
    }

    return this.persons$
      .getValue()
      .find(
        (p) => p.id.imsId === id.imsId && p.id.secondaryId === id.secondaryId,
      );
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemotePersons(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {
    await this.reconcileLocalPersons();
  }

  /**
   * Locally created persons have no backend id yet, so they can't be matched
   * to a synced person by id. Once the backend creates the real person (e.g.
   * via a form submission's postprocessor), it shows up as a separate synced
   * entry with the same full name - match on that and drop the local duplicate.
   */
  private async reconcileLocalPersons(): Promise<void> {
    const all = this.persons$.getValue();
    const localPersons = all.filter((person) => person.isLocal());
    if (localPersons.length === 0) {
      return;
    }
    const remotePersons = all.filter((person) => !person.isLocal());

    let didDelete = false;
    for (const local of localPersons) {
      const match = remotePersons.find(
        (remote) => remote.fullname?.trim().toLowerCase() === local.fullname?.trim().toLowerCase(),
      );
      if (match) {
        this.logger.log(
          this.constructor.name,
          'Found a remote person for local person, removing local duplicate',
          local.fullname,
        );
        await this.personsDaoService.deleteLocalPerson(local.id);
        didDelete = true;
      }
    }

    if (didDelete) {
      await this.reloadPersons();
    }
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalPersons();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  // Private Functions
  private async loadLocalPersons(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local persons');
      const persons = await this.personsDaoService.getPersons();
      this.persons$.next(persons);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local persons: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemotePersons(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    if (apiLevel < APILevel.PROJECT_CONTACT_DIRECTORY) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync persons',
      );
      return;
    }

    this.logger.log(this.constructor.name, '- Load new persons from IMS');

    try {
      const branchIdsStr = this.branchesService.branches$
        .getValue()
        .map((branch) => branch.getImsId())
        .join(',');

      const personsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncbranchpersons',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            branchidsstr: branchIdsStr,
            synckey: syncKey,
          },
        }),
      );

      const persons: Person[] = personsData.map((dto: any) => Person.fromDto(dto));

      this.logger.log(
        this.constructor.name,
        'Finished loading persons (' + persons.length + ' Total)',
      );

      await this.personsDaoService.savePersons(persons);
      const merged = await this.personsDaoService.getPersons();
      this.persons$.next(merged);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading persons: ',
        error?.message,
      );
      throw error;
    }
  }
}
